import React, { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  faBirthdayCake,
  faEllipsis,
  faExclamationCircle,
  faUserCheck,
  faUserClock,
  faUserXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";

import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { TnAAbsenceDTO } from "../../../models/tna";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { TnACalendarDaysDTO } from "../../../models/tna-calendar-days/tna-calendar-days-dto";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import { TnAScheduleIntervalRef } from "../../../models/tna/tna-schedule-intervals/tna-schedule-interval-ref";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../../reusable-components/Table/Table";
import { TnAOverviewsCalendarsApiService } from "../../../services/tna-overviews-calendars/tna-overviews-calendars-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { getDayAndMonth, getDaysInMonth, intlFormatDateMonth, intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";
import { ScheduleCodeWrapper } from "../../MyCalendar/DetailedFullCalendar/styles";
import ScheduleDetailsModal from "../../MyCalendar/ScheduleDetailsModal/ScheduleDetailsModal";
import QuickfiltersContainer from "../../Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { MyTnAContext } from "../MyTeamPresenceAndLeaves";
import {
  AbsencesContainer,
  ClientIconContainer,
  EmployeeNameContainer,
  MainTableContainer,
  ScheduleCode,
  StatusIndicator,
} from "./styles";
import { LightTooltip } from "../../AttachedDocuments/ButtonWithBadge/CustomTooltip/CustomTooltip";
import { EmployeeDTO } from "../../../models/employees";
import { TnADayDataRef } from "../../../models/tna/tna-data/tna-day-data-ref";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { RefModel } from "../../../models/base/ref-model";
import ExportTnACalendarToExcel from "./ExportTnACalendarToExcel/ExportTnACalendarToExcel";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import EditAbsenceWrapper from "./EditAbsenceWrapper/EditAbsenceWrapper";

interface State {
  loading: boolean;
  error: string;
}

const TnACalendar = () => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_calendar";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const theme = useTheme();

  const { forceUpdate } = useForceUpdate();

  const hasAccess = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const allowedToApproveReject = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allItems = useRef<TnACalendarDaysDTO[]>([]);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);

  const navigate = useNavigate();

  const clickNavigate = (employee: EmployeeDTO) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const columns: GridColumns = [
    {
      field: "employee.employeeCode, employee.internalId",
      headerName: `${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    clickNavigate(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      headerName: `${t(`common_words.employee`)} / ${t(`common_words.legal_entity`)}`,
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "employee.tnAGroup",
      headerName: t(`common_words.tna_employee_group`),
      headerAlign: "center",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  const dynamicColumns = (year: number, month: number) => {
    let result: GridColumns = [];
    const daysInMonth = getDaysInMonth(year, month);
    for (let i = 0; i < daysInMonth; i++) {
      result.push({
        field: new Date(year, month, i + 1).toString(),
        renderCell: ({ row }) => {
          const currDay = row?.daysData?.find((day: TnADayDataRef) => day?.tnADate?.day === i + 1);

          return !!currDay?.absences.length ? (
            <AbsencesContainer>
              {currDay?.absences.map((abs: TnAAbsenceDTO, idx: number) => {
                if (idx > 0) return;
                return (
                  <StatusIndicator
                    isRejected={abs.isRejected}
                    isCancelled={abs.isCancelled}
                    isApproved={abs.isApproved}
                    key={abs.id}
                  >
                    <EditAbsenceWrapper
                      absence={abs}
                      employee={row.employee}
                      handleNotifyParent={forceUpdate}
                      allowedToApproveReject={allowedToApproveReject}
                      viewMode={allowedToViewOnly && !hasAccess}
                      year={year}
                      month={month}
                      forManager={hasAccess}
                      allowedOnlyManagersAction
                    >
                      <ClientIconContainer>
                        <LightTooltip
                          title={
                            <Grid sx={{ display: "flex", flexDirection: "column", maxWidth: "300px" }}>
                              <Typography>
                                {abs.absenceType?.name +
                                  ": " +
                                  (abs.fromDate?.year !== abs.toDate?.year ||
                                  abs.fromDate?.month !== abs.toDate?.month ||
                                  abs.fromDate?.day !== abs.toDate?.day
                                    ? `${intlFormatDateMonth(
                                        CustomDate.ToDate(abs.fromDate) as Date
                                      )} - ${intlFormatDateMonth(CustomDate.ToDate(abs.toDate) as Date)}`
                                    : `${intlFormatDateMonth(CustomDate.ToDate(abs.fromDate) as Date)}`)}
                              </Typography>

                              <Typography>
                                Created by: <b>{abs.createdByUser?.name ?? abs.createdByUser?.email ?? ""}</b>
                              </Typography>

                              {abs.modifiedByUser && (abs.isApproved || abs.isRejected) && !abs.isCancelled && (
                                <>
                                  {abs.isApproved && (
                                    <Typography>
                                      Approved by: <b>{abs.modifiedByUser?.name ?? ""}</b>
                                    </Typography>
                                  )}

                                  {abs.isRejected && (
                                    <Typography>
                                      Rejected by: <b>{abs.modifiedByUser?.name ?? ""}</b>
                                    </Typography>
                                  )}
                                </>
                              )}
                            </Grid>
                          }
                        >
                          <Typography sx={{ fontSize: "0.7rem", lineHeight: "2em" }}>
                            {`${abs.absenceType?.code}${
                              !abs.isRejected && !abs.isCancelled && !abs.isApproved ? " ?" : ""
                            }`}
                          </Typography>
                        </LightTooltip>

                        {!!abs.countedDurationDays && (
                          <Typography
                            variant="body2"
                            title={`Counted days :  ${abs.countedDurationDays} day${
                              abs.countedDurationDays > 1 ? "s" : ""
                            }/ Total absence days : ${abs.calendaristicDays} day${
                              abs.calendaristicDays > 1 ? "s" : ""
                            }`}
                          >{` ${abs.countedDurationDays} day${abs.countedDurationDays > 1 ? "s" : ""} `}</Typography>
                        )}

                        <React.Fragment>
                          {abs?.needAcceptance &&
                            (abs?.acceptedByClient ? (
                              <FontAwesomeIcon
                                icon={faUserCheck}
                                color={"greenyellow"}
                                title={`Accepted by client ${
                                  abs.acceptedRejectedByUser?.name ?? abs.acceptedRejectedByEmail
                                }`}
                              />
                            ) : abs?.rejectedByClient ? (
                              <FontAwesomeIcon
                                icon={faUserXmark}
                                color={theme.palette.error.main}
                                title={`Rejected by client ${
                                  abs.acceptedRejectedByUser?.name ?? abs.acceptedRejectedByEmail
                                }`}
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faUserClock}
                                color={theme.palette.text.secondary}
                                title={"Pending client acceptance"}
                              />
                            ))}
                        </React.Fragment>
                      </ClientIconContainer>
                    </EditAbsenceWrapper>
                  </StatusIndicator>
                );
              })}

              {currDay?.absences.length > 1 && (
                <SimplePopover
                  buttonType="icon"
                  buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
                  flow="column"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  {currDay?.absences.slice(1).map((abs: TnAAbsenceDTO, idx: number) => {
                    return (
                      <div
                        style={{
                          marginLeft: "3px",
                          marginRight: "3px",
                          marginBottom: "2px",
                          marginTop: "2px",
                        }}
                        key={idx}
                      >
                        <StatusIndicator
                          isRejected={abs.isRejected}
                          isCancelled={abs.isCancelled}
                          isApproved={abs.isApproved}
                          key={abs.id}
                        >
                          <EditAbsenceWrapper
                            absence={abs}
                            employee={row.employee}
                            handleNotifyParent={forceUpdate}
                            allowedToApproveReject={allowedToApproveReject}
                            viewMode={allowedToViewOnly && !hasAccess}
                            year={year}
                            month={month}
                            forManager={hasAccess}
                            allowedOnlyManagersAction
                          >
                            <ClientIconContainer>
                              <Typography
                                title={
                                  abs.absenceType?.name +
                                  ": " +
                                  (abs.fromDate?.year !== abs.toDate?.year ||
                                  abs.fromDate?.month !== abs.toDate?.month ||
                                  abs.fromDate?.day !== abs.toDate?.day
                                    ? `${intlFormatDateMonth(
                                        CustomDate.ToDate(abs.fromDate) as Date
                                      )} - ${intlFormatDateMonth(CustomDate.ToDate(abs.toDate) as Date)}`
                                    : `${intlFormatDateMonth(CustomDate.ToDate(abs.fromDate) as Date)}`)
                                }
                                sx={{ fontSize: "0.6rem" }}
                              >
                                {`${abs.absenceType?.code}${
                                  !abs.isRejected && !abs.isCancelled && !abs.isApproved ? " ?" : ""
                                }`}
                              </Typography>
                              <React.Fragment>
                                {abs?.needAcceptance &&
                                  (abs?.acceptedByClient ? (
                                    <FontAwesomeIcon
                                      icon={faUserCheck}
                                      color={"greenyellow"}
                                    />
                                  ) : abs?.rejectedByClient ? (
                                    <FontAwesomeIcon
                                      icon={faUserXmark}
                                      color={theme.palette.error.main}
                                    />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={faUserClock}
                                      color={theme.palette.text.secondary}
                                    />
                                  ))}
                              </React.Fragment>
                            </ClientIconContainer>
                          </EditAbsenceWrapper>
                        </StatusIndicator>
                      </div>
                    );
                  })}
                </SimplePopover>
              )}
            </AbsencesContainer>
          ) : (
            <React.Fragment>
              <React.Fragment>
                {currDay?.hasAnomaly && (
                  <FontAwesomeIcon
                    className="fa-icon"
                    icon={faExclamationCircle}
                    style={{
                      marginLeft: 3,
                      opacity: 0.5,
                      color: theme.palette.error.main,
                    }}
                    title={`Anomaly: ${currDay.anomalyDescription}`}
                  />
                )}
              </React.Fragment>

              <React.Fragment>
                <Tooltip
                  title={currDay?.phNames as string}
                  placement="top"
                >
                  {currDay?.schedule ? (
                    <ScheduleCodeWrapper>
                      <ScheduleDetailsModal
                        intervals={currDay.schedule?.intervals as TnAScheduleIntervalRef[]}
                        name={currDay.schedule?.name as string}
                        description={currDay.schedule?.description as string}
                        code={currDay.schedule?.code as string}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <ScheduleCode title={`${currDay.schedule?.name}`}>{`${
                            currDay.schedule?.code?.trim() ? currDay.schedule?.code : "<?>"
                          }`}</ScheduleCode>
                        </div>
                      </ScheduleDetailsModal>
                    </ScheduleCodeWrapper>
                  ) : (
                    <Typography sx={{ fontSize: "0.75rem", width: "max-content !important" }}>
                      {currDay?.phNames as string}
                    </Typography>
                  )}
                </Tooltip>
              </React.Fragment>
              {/* ))} */}
            </React.Fragment>
          );
        },
        headerName: getDayAndMonth(new Date(year, month, i + 1)),
        headerAlign: "center",
        align: "center",
        flex: 1,
        minWidth: 90,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params: any) => {
          const currDay = new Date(params.field);
          const dayOfWeek: number = currDay.getDay();
          const thisDayData = params.row?.daysData?.find(
            (day: TnADayDataRef) => day?.tnADate?.day === currDay.getDate()
          );

          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return thisDayData?.isPublicHoliday ? "isPublicHoliday" : isWeekend ? "isWeekend" : "";
        },
      });
    }

    return result;
  };

  const defaultItem: Partial<TnACalendarDaysDTO> = {
    employee: null,
    daysData: [],
  };

  const loadEmployeeGroup = useCallback(async () => {
    try {
      const response = await new EmployeeGroupsApiService().loadItems({
        ids: employeeGroupOption?.employeeGroupIds,
      });

      if (!response.error) {
        return response.items[0];
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    }
  }, [employeeGroupOption?.employeeGroupIds]);

  const loadTnACalendarsData = useCallback(
    async (pageSize: number = 10) => {
      try {
        const response = await new TnAOverviewsCalendarsApiService().loadItems(
          {
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
            onlyCurrentEmployees: !!onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
            year: year,
            month: month + 1,
          } as TnAOverviewsFilter,
          "",
          [],
          {
            pageNumber: 0,
            pageSize: pageSize,
          }
        );

        if (!response.error) {
          return response;
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [
      employeeGroupOption?.employeeGroupIds,
      onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees,
      legalEntityOption?.legalEntityIds,
      year,
      month,
    ]
  );

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            {!!legalEntityQuickFilter && legalEntityQuickFilter}
            {!!employeeGroupQuickFilter && employeeGroupQuickFilter}
            {!!onlyCurrentEmployeesQuickFilter && onlyCurrentEmployeesQuickFilter}
            {!!yearMonthQuickFilter && yearMonthQuickFilter}
            <ExportTnACalendarToExcel
              loadEmployeeGroup={loadEmployeeGroup}
              loadTnACalendarsData={loadTnACalendarsData}
              disabled={state.loading || loading || !items?.length}
              allItems={() => allItems.current}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [state.loading, legalEntityQuickFilter, employeeGroupQuickFilter, onlyCurrentEmployeesQuickFilter]
  );

  return (
    <LoadingOverlay
      loading={state.loading}
      error={state.error}
    >
      <MainTableContainer>
        {(hasAccess || allowedToViewOnly) && (
          <Table
            translationRootPath={tRP}
            columns={columns}
            serviceApi={new TnAOverviewsCalendarsApiService()}
            propsFilter={
              {
                ...legalEntityOption,
                ...employeeGroupOption,
                ...onlyCurrentEmployeesOptionValue,
                year,
                month: month + 1,
              } as TnAOverviewsFilter
            }
            defaultItem={defaultItem}
            filter={FT_MTPAL}
            readOnly
            quickFilters={quickfilters}
            dynamicColumns={dynamicColumns(year, month)}
            getAllItems={(items) => {
              allItems.current = items as TnACalendarDaysDTO[];
            }}
          />
        )}
      </MainTableContainer>
    </LoadingOverlay>
  );
};

export default TnACalendar;
